import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { KeyRound, ArrowLeft, Mail } from "lucide-react";
import LegalStrip from "@/components/LegalStrip";

export default function ForgotPassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email | reset
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success(t("forgot.sent_msg"));
      setStep("reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (newPw !== confirm) return toast.error(t("toast.passwords_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPw });
      toast.success(t("reset.done"));
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="forgot-page">
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1583902340370-6a3fc847d373?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwY29pbiUyMHN0YWNrfGVufDB8fHx8MTc4NTc1MDE5OXww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,5,5,0.85), rgba(5,5,5,0.6) 60%, rgba(5,5,5,0.95))" }} />
        <div className="relative z-10">
          <div className="font-display text-4xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
          <div className="label-caps mt-2">{t("app.tagline")}</div>
        </div>
        <div className="relative z-10 max-w-md">
          <KeyRound size={40} style={{color:"var(--gold)"}} />
          <h1 className="font-display text-5xl leading-tight font-light mt-6">
            <span style={{color:"var(--gold)"}}>{t("forgot.title")}</span>
          </h1>
          <p className="text-neutral-400 mt-6 leading-relaxed">{t("forgot.subtitle")}</p>
        </div>
        <div className="relative z-10 label-caps">{t("footer.copyright")}</div>
      </div>

      <div className="flex flex-col lg:items-center lg:justify-center p-6 sm:p-8 lg:p-16 bg-[#0a0a0a] relative min-h-screen">
        <div className="absolute right-4 sm:right-6 z-20 safe-top-abs"><LanguageSwitcher compact /></div>
        <div className="lg:hidden absolute left-4 sm:left-6 z-20 safe-top-abs">
          <div className="font-display text-xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
        </div>
        <div className="order-last lg:order-none lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:px-8 mt-auto pt-8 w-full"><LegalStrip /></div>

        {step === "email" && (
          <form onSubmit={sendCode} className="w-full max-w-sm space-y-6 safe-top-form" data-testid="forgot-form">
            <div>
              <div className="w-12 h-12 border flex items-center justify-center mb-4" style={{borderColor:"var(--gold)", color:"var(--gold)"}}><KeyRound size={22}/></div>
              <div className="label-caps">{t("security.title")}</div>
              <h2 className="font-display text-3xl mt-2">{t("forgot.title")}</h2>
              <p className="text-xs text-neutral-400 mt-2">{t("forgot.subtitle")}</p>
            </div>
            <div>
              <label className="label-caps block mb-2">{t("auth.email")}</label>
              <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                className="w-full bg-[--surface] border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                data-testid="forgot-email" style={{background:"var(--surface)"}} />
            </div>
            <button type="submit" disabled={loading} className="btn-primary-gold w-full" data-testid="forgot-submit">
              {loading ? t("auth.connecting") : t("forgot.send")}
            </button>
            <Link to="/login" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-[--gold] justify-center pt-2" data-testid="back-to-login">
              <ArrowLeft size={14} /> {t("auth.login")}
            </Link>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={reset} className="w-full max-w-sm space-y-5 safe-top-form" data-testid="reset-form">
            <div>
              <div className="w-12 h-12 border flex items-center justify-center mb-4" style={{borderColor:"var(--gold)", color:"var(--gold)"}}><Mail size={22}/></div>
              <h2 className="font-display text-3xl">{t("reset.new_pw")}</h2>
              <p className="text-xs text-neutral-400 mt-2">{t("verify.subtitle")}</p>
              <p className="text-sm mt-3 font-mono" style={{color:"var(--gold)"}}>{email}</p>
            </div>
            <div>
              <label className="label-caps block mb-2">{t("reset.code")}</label>
              <input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                value={code} onChange={(e)=>setCode(e.target.value.replace(/[^0-9]/g,""))}
                data-testid="reset-code"
                className="w-full bg-[--surface] border border-white/10 px-4 py-3 text-center text-xl font-mono tracking-[0.4em] focus:outline-none focus:ring-1 focus:ring-[--gold]"
                style={{background:"var(--surface)"}} placeholder="000000" />
            </div>
            <div>
              <label className="label-caps block mb-2">{t("reset.new_pw")}</label>
              <input required type="password" value={newPw} onChange={(e)=>setNewPw(e.target.value)}
                className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                data-testid="reset-new-pw" style={{background:"var(--surface)"}} />
              <div className="text-xs text-neutral-500 mt-1">{t("signup.password_hint")}</div>
            </div>
            <div>
              <label className="label-caps block mb-2">{t("auth.password_confirm")}</label>
              <input required type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                className="w-full bg-[--surface] border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
                data-testid="reset-confirm" style={{background:"var(--surface)"}} />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} className="btn-primary-gold w-full" data-testid="reset-submit">
              {loading ? t("auth.connecting") : t("reset.submit")}
            </button>
            <button type="button" onClick={()=>setStep("email")} className="w-full text-xs text-neutral-500 hover:text-white">
              {t("verify.change_email")}
            </button>
          </form>
        )}
      </div>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
